/**
 * Topology change signal for the web app.
 *
 * Views that cache "which device sits where" (the fleet roster's site / delivery
 * point / dispatch group enrichment, Microgrid, Dispatcher device lists) must
 * refresh when a device is linked to / unlinked from a circuit, a circuit or
 * delivery point is created or deleted, a device is deleted, or a dispatch
 * group's delivery points change. Before this, the roster kept its enrichment
 * for the whole browser session, so an unlinked device stayed on its old site
 * until a full page reload.
 *
 * `installTopologySync()` watches the generated grpc-zustand stores: every
 * successful topology mutation replaces that method's `data`, which bumps a
 * version number. Views read it with `useTopologyVersion()` and refetch. The
 * bump is also broadcast to other tabs of the app (BroadcastChannel), so an edit
 * made in Domain Entities in one tab refreshes Microgrid open in another.
 */

import { useCircuitServiceStore } from "@fermata/grpc-zustand/circuit"
import { useCircuitDeviceServiceStore } from "@fermata/grpc-zustand/circuitDevice"
import { useDeliveryPointServiceStore } from "@fermata/grpc-zustand/deliveryPoint"
import { useDeviceServiceStore } from "@fermata/grpc-zustand/device"
import { useDispatchGroupServiceStore } from "@fermata/grpc-zustand/dispatchGroup"
import { useSiteServiceStore } from "@fermata/grpc-zustand/site"
import { useSyncExternalStore } from "react"

let version = 0
const listeners = new Set<() => void>()
const CHANNEL = "fe2-topology"
let channel: BroadcastChannel | null = null

function bump(): void {
  version++
  for (const l of listeners) l()
}

/** Signal that topology changed (also tells other open tabs). */
export function notifyTopologyChanged(): void {
  bump()
  try {
    channel?.postMessage(version)
  } catch {
    /* channel closed / unsupported: local bump already happened */
  }
}

/** Current topology version; changes after every topology mutation. */
export function getTopologyVersion(): number {
  return version
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener)
  return () => listeners.delete(listener)
}

/** React hook: re-renders (and lets effects re-run) when topology changes. */
export function useTopologyVersion(): number {
  installTopologySync()
  return useSyncExternalStore(subscribe, getTopologyVersion, getTopologyVersion)
}

type Slot = { data?: unknown }
type AnyStore = {
  subscribe: (listener: (state: unknown, prev: unknown) => void) => () => void
}

/** Mutation methods whose success changes where devices sit. */
const WATCHED: Array<[AnyStore, string[]]> = [
  [
    useCircuitDeviceServiceStore as unknown as AnyStore,
    ["createCircuitDevice", "updateCircuitDevice", "deleteCircuitDevice"],
  ],
  [
    useCircuitServiceStore as unknown as AnyStore,
    ["createCircuit", "updateCircuit", "deleteCircuit"],
  ],
  [
    useDeliveryPointServiceStore as unknown as AnyStore,
    ["createDeliveryPoint", "updateDeliveryPoint", "deleteDeliveryPoint"],
  ],
  [
    useDeviceServiceStore as unknown as AnyStore,
    [
      "createDevice",
      "deleteDevice",
      "restoreDevice",
      "createStation",
      "updateStation",
      "deleteStation",
      "createEvse",
      "updateEvse",
      "deleteEvse",
      "createConnector",
      "updateConnector",
      "deleteConnector",
      "createStationaryBattery",
      "updateStationaryBattery",
      "deleteStationaryBattery",
    ],
  ],
  [
    useDispatchGroupServiceStore as unknown as AnyStore,
    [
      "createDispatchGroup",
      "deleteDispatchGroup",
      "restoreDispatchGroup",
      "addDispatchGroupDeliveryPoint",
      "removeDispatchGroupDeliveryPoint",
    ],
  ],
  [useSiteServiceStore as unknown as AnyStore, ["deleteSite"]],
]

let installed = false

/** Idempotent: start watching the stores (and other tabs) once per page. */
export function installTopologySync(): void {
  if (installed) return
  installed = true
  for (const [store, methods] of WATCHED) {
    store.subscribe((state, prev) => {
      const s = state as Record<string, Slot | undefined>
      const p = prev as Record<string, Slot | undefined>
      // A successful call replaces `data`; failures leave it untouched.
      if (methods.some((m) => s[m]?.data !== undefined && s[m]?.data !== p[m]?.data)) {
        notifyTopologyChanged()
      }
    })
  }
  try {
    channel = new BroadcastChannel(CHANNEL)
    channel.onmessage = () => bump()
  } catch {
    channel = null // BroadcastChannel unavailable (old browser / tests)
  }
}
